import {
  Box,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  Link,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import DoneIcon from '@material-ui/icons/Done';
import ErrorIcon from '@material-ui/icons/Error';
import SearchIcon from '@material-ui/icons/Search';
import JsonForm, { getDefaultRegistry, IChangeEvent } from '@rjsf/core';
import dayjs from 'dayjs';
import {
  ADDITIONAL_PROPERTY_FLAG,
  ariaDescribedByIds,
  ArrayFieldTemplateItemType,
  ArrayFieldTemplateProps,
  BaseInputTemplateProps,
  CustomValidator,
  DescriptionFieldProps,
  descriptionId,
  enumOptionsIndexForValue,
  enumOptionsValueForIndex,
  ErrorListProps,
  Experimental_CustomMergeAllOf,
  FieldProps,
  FieldTemplateProps,
  GenericObjectType,
  getInputProps,
  getSubmitButtonOptions,
  getTemplate,
  getUiOptions,
  labelValue,
  ObjectFieldTemplateProps,
  optionId,
  RJSFSchema,
  schemaRequiresTrueValue,
  SubmitButtonProps,
  TitleFieldProps,
  titleId,
  UiSchema,
  WidgetProps,
  WrapIfAdditionalTemplateProps,
} from '@rjsf/utils';
import { customizeValidator } from '@rjsf/validator-ajv8';
import AJV8Validator from '@rjsf/validator-ajv8/lib/validator';
import ajvErrors from 'ajv-errors';
import { merge } from 'allof-merge';
import {
  ChangeEvent,
  createContext,
  createRef,
  FocusEvent,
  ReactNode,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ReactMarkdown from 'react-markdown';
import NumberFormat from 'react-number-format';
import IconBlueTick from '../assets/images/icon-blue-tick.svg';
import useForm from '../hooks/useForm';
import useProfile from '../hooks/useProfile';
import currencySymbol from '../services/util/currencySymbol';
import getSharepointDownloadUrl from '../services/getSharepointDownloadUrl';
import isDocumentViewerEnabled from '../services/isDocumentViewerEnabled';
import resolveDocumentId from '../services/util/resolveDocumentId';
import Button from './Button';
import Document, { DocumentProps } from './Document';
import Modal from './Modal';
import Signature from './Signature';
import InfoBox from './InfoBox';
import {
  InfoIcon,
  TickGreenCircleIcon,
  CircleRingIcon,
} from '../theme/styled-components';

interface PasswordPolicy {
  MinimumLength?: number;
  RequireLowercase?: boolean;
  RequireUppercase?: boolean;
  RequireNumbers?: boolean;
  RequireSymbols?: boolean;
  TemporaryPasswordValidityDays?: number;
  __typename?: string;
}

interface FormContextWithPasswordPolicy {
  passwordPolicy?: PasswordPolicy;
  formData?: GenericObjectType;
  [key: string]: unknown;
}

function isPasswordField(
  type: string | undefined,
  schemaFormat: string | undefined,
  uiWidget: string | undefined,
  id: string | undefined,
) {
  return (
    type === 'password' ||
    schemaFormat === 'password' ||
    uiWidget === 'password' ||
    !!id?.includes('Password') ||
    !!id?.includes('password')
  );
}

function validatePasswordRequirement(
  rule: string,
  passwordValue: string,
  passwordPolicy: PasswordPolicy | undefined,
  isConfirmPassword: boolean,
  otherPasswordValue?: string,
) {
  if (!passwordValue || passwordValue.length === 0) {
    return false;
  }

  switch (rule) {
    case 'length':
      return passwordValue.length >= (passwordPolicy?.MinimumLength || 8);
    case 'lowercase':
      return passwordPolicy?.RequireLowercase
        ? /([a-z])/g.test(passwordValue)
        : true;
    case 'uppercase':
      return passwordPolicy?.RequireUppercase
        ? /([A-Z])/g.test(passwordValue)
        : true;
    case 'number':
      return passwordPolicy?.RequireNumbers
        ? /([0-9])/g.test(passwordValue)
        : true;
    case 'specialCharacters':
      return passwordPolicy?.RequireSymbols ? /\W/g.test(passwordValue) : true;
    case 'oneOf':
      return isConfirmPassword && otherPasswordValue
        ? passwordValue === otherPasswordValue
        : true;
    default:
      return true;
  }
}

interface PasswordValidationItemProps {
  isValid: boolean;
  text: string;
}

function PasswordValidationItem({
  isValid,
  text,
}: PasswordValidationItemProps) {
  const classes = useStyles();
  return (
    <dd>
      <Box className={classes.passwordErrorBox}>
        {isValid ? <TickGreenCircleIcon /> : <CircleRingIcon />}
        {text}
      </Box>
    </dd>
  );
}

interface PasswordValidationListProps {
  passwordValue: string;
  passwordPolicy: PasswordPolicy;
  isConfirmPassword: boolean;
  otherPasswordValue?: string;
}

function PasswordValidationList({
  passwordValue,
  passwordPolicy,
  isConfirmPassword,
  otherPasswordValue,
}: PasswordValidationListProps) {
  const classes = useStyles();

  if (isConfirmPassword) {
    const isValid = validatePasswordRequirement(
      'oneOf',
      passwordValue,
      passwordPolicy,
      true,
      otherPasswordValue,
    );
    return (
      <dl className={classes.passwordList}>
        <PasswordValidationItem
          isValid={isValid}
          text="Confirmation password must match"
        />
      </dl>
    );
  }

  const validateRequirement = (rule: string) =>
    validatePasswordRequirement(
      rule,
      passwordValue,
      passwordPolicy,
      false,
      otherPasswordValue,
    );

  return (
    <dl className={classes.passwordList}>
      <PasswordValidationItem
        isValid={validateRequirement('length')}
        text={`Needs at least ${passwordPolicy.MinimumLength || 8} characters`}
      />
      {passwordPolicy.RequireSymbols && (
        <PasswordValidationItem
          isValid={validateRequirement('specialCharacters')}
          text="At least one special character (^*$#£@\!)"
        />
      )}
      {passwordPolicy.RequireNumbers && (
        <PasswordValidationItem
          isValid={validateRequirement('number')}
          text="At least one number (0-9)"
        />
      )}
      {passwordPolicy.RequireUppercase && (
        <PasswordValidationItem
          isValid={validateRequirement('uppercase')}
          text="At least one upper case letter (A-Z)"
        />
      )}
      {passwordPolicy.RequireLowercase && (
        <PasswordValidationItem
          isValid={validateRequirement('lowercase')}
          text="At least one lower case letter (a-z)"
        />
      )}
    </dl>
  );
}

const useStyles = makeStyles((theme) => ({
  arrayItem: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: theme.palette.common.backgroundNotification,
    borderRadius: 5,
    padding: '24px 40px',
    justifyContent: 'space-between',
    width: '100%',
  },
  arrayTitle: {
    fontSize: 18,
    fontWeight: 'normal',
    margin: '12px 0',
  },
  arrayWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  allocationTotal: {
    fontSize: 22,
    margin: '12px 0',
    borderBottom: `4px solid`,
    minWidth: 100,
    textAlign: 'right',
    '&.warning': {
      borderColor: theme.palette.common.yellow,
    },
    '&.danger': {
      borderColor: theme.palette.common.coral,
    },
    '&.success': {
      borderColor: theme.palette.common.green,
    },
  },
  allocationTotalWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  allocationWrapper: {
    display: 'flex',
    alignItems: 'center',
    '& .input': {
      width: 20,
    },
  },
  buttonBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '48px 0 0',
  },
  checkbox: {
    display: 'flex',
    position: 'relative',
    '& .input': {
      opacity: 0,
      position: 'absolute',
      '&:checked + .label:before': {
        backgroundColor: '#59D09E',
        backgroundImage: `url("${IconBlueTick}")`,
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '90% 90%',
      },
      '&:disabled + .label:before': {
        opacity: 0.5,
      },
    },
    '& .label': {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 24,
      '&:before': {
        alignItems: 'center',
        border: `2px solid ${
          theme.palette.type === 'light'
            ? theme.palette.common.midBlue
            : theme.palette.common.white
        }`,
        content: '""',
        cursor: 'pointer',
        display: 'inline-block',
        flex: 1,
        height: 20,
        padding: 10,
        position: 'relative',
        width: 20,
        '& span': {
          display: 'block',
        },
      },
    },
  },
  // A checkbox description is body copy, not a heading, so it sits at label size.
  checkboxDescription: {
    fontSize: 16,
    margin: '16px 0',
    '& > p:not(:last-child)': {
      marginBottom: 8,
    },
  },
  signed: {
    '& > p:last-child': {
      marginTop: 16,
    },
  },
  radioCircle: {
    display: 'flex',
    position: 'relative',
    '& .input': {
      opacity: 0,
      position: 'absolute',
      '&:checked + .label:after': {
        opacity: 1,
      },
      '&:disabled + .label': {
        opacity: 0.5,
      },
    },
    '& .label': {
      display: 'flex',
      alignItems: 'center',
      gap: 16,
      cursor: 'pointer',
      position: 'relative',
      paddingLeft: 40,
      '&:before': {
        border: `2px solid ${theme.palette.common.white}`,
        borderRadius: '50%',
        boxSizing: 'border-box',
        content: '""',
        position: 'absolute',
        left: 0,
        height: 24,
        width: 24,
      },
      '&:after': {
        content: '""',
        position: 'absolute',
        left: 6,
        top: '50%',
        transform: 'translateY(-50%)',
        height: 12,
        width: 12,
        borderRadius: '50%',
        backgroundColor: theme.palette.common.white,
        opacity: 0,
        transition: 'opacity 0.15s ease-in-out',
      },
    },
  },
  documentWrapper: {
    margin: '0 0 24px',
  },
  editButton: {
    minWidth: 'auto',
    textTransform: 'none',
  },
  errorButton: {
    display: 'inline-flex',
    gap: 4,
  },
  fieldset: {
    border: 0,
    margin: 0,
    padding: 0,
  },
  form: {
    width: '100%',
    '&.inline': {
      display: 'flex',
      gap: 18,
    },
  },
  hidden: {
    display: 'none',
    visibility: 'hidden',
  },
  helpText: {
    fontSize: 22,
    color: '#FAC81E',
    margin: '12px 0',
  },
  icon: {
    color: '#fff',
    fill: '#fff',
  },
  infoIcon: {
    display: 'inline-block !important',
    marginLeft: 16,
  },
  label: {
    color: theme.palette.common.grey,
  },
  inlineLabel: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: '8px',
  },
  inlineLabelReadOnly: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: 0,
  },
  list: {
    listStyleType: 'disc',
    paddingLeft: theme.spacing(2),
    paddingTop: 0,
  },
  orderedList: {
    listStyleType: 'decimal',
    paddingLeft: theme.spacing(3),
    paddingTop: 0,
  },
  listItem: {
    display: 'list-item',
    listStyleType: 'inherit',
    padding: 0,
    background: 'transparent',
    marginBottom: 8,
  },
  parent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  radio: {
    marginRight: 31,
  },
  row: {
    borderBottom: `1px solid ${theme.palette.common.backgroundDark}`,
    padding: '12px 0',
  },
  searchIcon: {
    display: 'block',
    left: 15,
    position: 'absolute',
    top: 20,
    zIndex: 2,
  },
  signature: {
    alignItems: 'center',
    display: 'flex',
    gap: 26,
  },
  signatureImage: {
    display: 'block',
    height: 75,
    margin: '-18.25px 0',
  },
  textbox: {
    backgroundColor: theme.palette.common.backgroundNotification,
    borderRadius: 8,
    color: theme.palette.common.tan,
    padding: '6px 12px',
    width: 250,
    '&.read-only': {
      padding: '12px 0 0',
      backgroundColor: 'transparent',
      [theme.breakpoints.up('sm')]: {
        padding: 0,
      },
      '& .MuiInput-input:read-only': {
        padding: 0,
      },
    },
    '&.search': {
      backgroundColor: theme.palette.common.backgroundDark,
      borderRadius: 22,
      padding: '4px 0 4px 45px',
      width: '100%',
    },
    '&.darkBackground': {
      backgroundColor: theme.palette.common.backgroundHighlight,
      width: 130,
    },
    '&.MuiFormControl-fullWidth': {
      width: '100% !important',
    },
  },
  title: {
    fontSize: 22,
    margin: '12px 0',
    '& span': {
      fontSize: 16,
    },
  },
  tooltip: {
    backgroundColor: theme.palette.common.tan,
    color: theme.palette.common.backgroundHighlight,
  },
  tooltipAnchor: {
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  tooltipArrow: {
    color: theme.palette.common.tan,
  },
  totalWrapper: {
    display: 'inline-flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  wrapper: {
    alignItems: 'start',
    backgroundColor: theme.palette.common.backgroundNotification,
    borderRadius: 5,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: '24px 40px',
  },
  radioBoxWrapper: {
    backgroundColor: theme.palette.common.backgroundNotification,
    borderRadius: 5,
    padding: '24px 40px',
    '& .MuiFormControlLabel-root': {
      margin: 0,
    },
    '& .MuiRadio-root': {
      padding: '0 24px 0 0',
    },
  },
  passwordList: {
    margin: 0,
    marginTop: 22,
    padding: 0,
    display: 'block',
    '& dd': {
      margin: 0,
      marginBottom: 10,
      textAlign: 'left',
    },
  },
  passwordErrorBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    '& > :first-child': {
      flexShrink: 0,
    },
  },
}));

interface ReadOnlyTooltipProps {
  children: ReactNode;
  readonly?: boolean;
}

function ReadOnlyTooltip({ children, readonly = false }: ReadOnlyTooltipProps) {
  const classes = useStyles();

  if (readonly) {
    return (
      <Tooltip
        arrow
        classes={{
          tooltip: classes.tooltip,
          arrow: classes.tooltipArrow,
        }}
        placement="right"
        title="This field has been input by Saltus. If you feel this is incorrect, please contact your adviser and do not sign this form"
      >
        <span className={classes.tooltipAnchor}>{children}</span>
      </Tooltip>
    );
  }

  return <>{children}</>;
}

function ArrayFieldTemplate({
  idSchema,
  items,
  registry,
  required,
  schema,
  title,
  uiSchema,
  ...otherProps
}: ArrayFieldTemplateProps & { formRef: RefObject<JsonForm> }) {
  const classes = useStyles();

  const options = getUiOptions(uiSchema);

  const ArrayFieldDescriptionTemplate = getTemplate(
    'ArrayFieldDescriptionTemplate',
    registry,
    options,
  );

  const ArrayFieldItemTemplate = getTemplate(
    'ArrayFieldItemTemplate',
    registry,
    options,
  );

  const ArrayFieldTitleTemplate = getTemplate(
    'ArrayFieldTitleTemplate',
    registry,
    options,
  );

  return (
    <fieldset id={idSchema.$id} className={classes.fieldset}>
      <div className={classes.parent}>
        {/[A-Z]/.test(options.title || title) && (
          <ArrayFieldTitleTemplate
            idSchema={idSchema}
            title={options.title || title}
            required={required}
            schema={schema}
            uiSchema={uiSchema}
            registry={registry}
          />
        )}

        <ArrayFieldDescriptionTemplate
          idSchema={idSchema}
          description={options.description || schema.description}
          schema={schema}
          uiSchema={uiSchema}
          registry={registry}
        />

        {options.template === 'ExpressionOfWish' ? (
          <ExpressionOfWishFieldTemplate
            idSchema={idSchema}
            items={items}
            registry={registry}
            required={required}
            schema={schema}
            title={title}
            uiSchema={uiSchema}
            {...otherProps}
          />
        ) : (
          items &&
          items.map(({ key, ...itemProps }) => (
            <ArrayFieldItemTemplate key={key} {...itemProps} />
          ))
        )}
      </div>
    </fieldset>
  );
}

function BaseInputTemplate({
  autofocus,
  color,
  defaultChecked,
  defaultValue,
  disabled,
  formContext,
  hideError,
  hideLabel,
  id,
  label,
  options,
  placeholder,
  rawErrors,
  readonly,
  schema,
  type,
  uiSchema,
  value,
  onBlur,
  onChange,
  onChangeOverride,
  onFocus,
  ...rest
}: BaseInputTemplateProps) {
  const classes = useStyles();

  const inputProps = getInputProps(schema, type as string, options);
  const { type: unusedType1, ...restProps } = inputProps;
  const { type: unusedType2, ...restWithoutType } = rest;
  const props = {
    ...restWithoutType,
    ...restProps,
  };

  const onTextBlur = (event: FocusEvent<HTMLInputElement>) => {
    onBlur(id, event.target.value);
  };

  const onTextChange = (event: ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;

    onChange(val === '' ? options.emptyValue || '' : val);
  };

  const onTextFocus = (event: FocusEvent<HTMLInputElement>) => {
    onFocus(id, event.target.value);
  };

  const uiOptions = uiSchema ? getUiOptions(uiSchema) : {};
  const mergedOptions = { ...uiOptions, ...options } as GenericObjectType;
  const typedFormContext = formContext as
    | FormContextWithPasswordPolicy
    | undefined;
  const passwordPolicy =
    (mergedOptions?.passwordPolicy as PasswordPolicy | undefined) ||
    typedFormContext?.passwordPolicy;

  const uiWidget = uiSchema?.['ui:widget'];
  const uiWidgetString = typeof uiWidget === 'string' ? uiWidget : undefined;
  const fieldIsPassword = isPasswordField(
    type as string | undefined,
    schema.format,
    uiWidgetString,
    id,
  );
  const isConfirmPassword = id?.includes('confirm') || false;

  const passwordValue = typeof value === 'string' ? value : '';
  const formData = typedFormContext?.formData as
    | { newPassword?: string }
    | undefined;
  const otherPasswordValue = isConfirmPassword
    ? formData?.newPassword
    : undefined;

  if (options.inputMask) {
    return (
      <NumberFormat
        id={id}
        className={[classes.textbox, readonly ? 'read-only' : ''].join(' ')}
        customInput={TextField}
        InputProps={{
          disableUnderline: true,
          readOnly: readonly,
          inputProps: {
            'data-testid': id,
          },
        }}
        allowLeadingZeros
        isNumericString
        disabled={disabled}
        format={options.inputMask as string}
        onValueChange={(values) =>
          onChange(
            options.formattedValue ? values.formattedValue : values.value,
          )
        }
        placeholder={placeholder}
        variant="standard"
      />
    );
  }

  const inputType = fieldIsPassword ? 'password' : (type as string | undefined);
  const { type: unusedType, ...propsWithoutType } = props as {
    type?: string;
    InputProps?: {
      inputProps?: {
        [key: string]: unknown;
      };
      [key: string]: unknown;
    };
    inputProps?: {
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  const {
    inputProps: existingInputProps,
    InputProps: existingTextFieldInputProps,
    ...textFieldProps
  } = propsWithoutType;

  const mergedTextFieldInputProps = {
    ...(existingTextFieldInputProps?.inputProps ?? {}),
    ...(existingInputProps ?? {}),
    'data-testid': id,
  };

  const inputField = (
    <TextField
      id={id}
      type={inputType}
      className={[
        classes.textbox,
        readonly ? 'read-only' : '',
        options.search ? 'search' : '',
      ].join(' ')}
      InputProps={{
        ...(existingTextFieldInputProps ?? {}),
        disableUnderline: true,
        readOnly: readonly,
        inputProps: mergedTextFieldInputProps,
      }}
      value={(value as unknown) ?? ''}
      variant="standard"
      placeholder={placeholder}
      disabled={disabled}
      onChange={onChangeOverride || onTextChange}
      onBlur={onTextBlur}
      onFocus={onTextFocus}
      fullWidth={!!options.fullWidth}
      multiline={!!options.multiline}
      minRows={options.multiline ? 4 : undefined}
      autoComplete={fieldIsPassword ? 'off' : undefined}
      {...textFieldProps}
    />
  );

  if (fieldIsPassword && passwordPolicy) {
    return (
      <div>
        {inputField}
        <PasswordValidationList
          passwordValue={passwordValue}
          passwordPolicy={passwordPolicy}
          isConfirmPassword={isConfirmPassword}
          otherPasswordValue={otherPasswordValue}
        />
      </div>
    );
  }

  return inputField;
}

function CheckboxWidget({
  schema,
  options,
  id,
  value,
  disabled,
  readonly,
  label,
  hideLabel,
  onBlur,
  onFocus,
  onChange,
}: WidgetProps) {
  const classes = useStyles();

  const required = schemaRequiresTrueValue(schema);

  const description = options.description ?? schema.description;

  // Allow overriding label via ui:options
  const displayLabel =
    typeof options.label === 'string' ? options.label : label;

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      onBlur(id, event.target.checked);
    },
    [onBlur, id],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.checked);
    },
    [onChange],
  );

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      onFocus(id, event.target.checked);
    },
    [onFocus, id],
  );

  const markdown = options.markdown === true;

  const rich = (text: ReactNode) =>
    markdown && typeof text === 'string' ? (
      <ReactMarkdown components={markdownComponents(classes)}>
        {text}
      </ReactMarkdown>
    ) : (
      text
    );

  return (
    <>
      {!hideLabel && !!description && (
        <Typography
          id={descriptionId(id)}
          className={
            options.signed === true
              ? `${classes.checkboxDescription} ${classes.signed}`
              : classes.checkboxDescription
          }
          component={markdown ? 'div' : 'p'}
        >
          {rich(description)}
        </Typography>
      )}

      <div className={classes.checkbox}>
        <input
          id={id}
          className="input"
          type="checkbox"
          data-testid={id}
          name={id}
          checked={typeof value === 'undefined' ? false : (value as boolean)}
          required={required}
          tabIndex={0}
          disabled={disabled || readonly}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          aria-describedby={ariaDescribedByIds(id)}
        />

        <label className="label" htmlFor={id}>
          {labelValue(
            markdown ? (
              <div>{rich(displayLabel)}</div>
            ) : (
              <span>{displayLabel}</span>
            ),
            hideLabel,
          )}
        </label>
      </div>
    </>
  );
}

function DateWidget({ value }: WidgetProps) {
  const classes = useStyles();
  return (
    <TextField
      className={[classes.textbox, 'read-only'].join(' ')}
      InputProps={{
        disableUnderline: true,
        readOnly: true,
      }}
      value={
        value && dayjs(value as string).isValid()
          ? dayjs(value as string).format('DD/MM/YYYY')
          : ''
      }
      variant="standard"
    />
  );
}

function PasswordWidget({
  id,
  value,
  onChange,
  onBlur,
  onFocus,
  disabled,
  readonly,
  placeholder,
  options,
  formContext,
}: WidgetProps) {
  const classes = useStyles();

  const typedFormContext = formContext as
    | FormContextWithPasswordPolicy
    | undefined;
  const passwordPolicyFromOptions = options?.passwordPolicy as
    | PasswordPolicy
    | undefined;
  const passwordPolicy =
    passwordPolicyFromOptions || typedFormContext?.passwordPolicy;

  const isConfirmPassword = id?.includes('confirm') || false;
  const formData = typedFormContext?.formData as
    | { newPassword?: string }
    | undefined;
  const otherPasswordValue = isConfirmPassword
    ? formData?.newPassword
    : undefined;

  const passwordValue = typeof value === 'string' ? value : '';

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value);
    },
    [onChange],
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      onBlur(id, event.target.value);
    },
    [onBlur, id],
  );

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      onFocus(id, event.target.value);
    },
    [onFocus, id],
  );

  return (
    <div>
      <TextField
        id={id}
        type="password"
        className={classes.textbox}
        InputProps={{
          disableUnderline: true,
          readOnly: readonly,
          inputProps: {
            'data-testid': id,
          },
        }}
        value={passwordValue}
        variant="standard"
        placeholder={placeholder}
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        fullWidth={!!options?.fullWidth}
        autoComplete="off"
      />

      {passwordPolicy && (
        <PasswordValidationList
          passwordValue={passwordValue}
          passwordPolicy={passwordPolicy}
          isConfirmPassword={isConfirmPassword}
          otherPasswordValue={otherPasswordValue}
        />
      )}
    </div>
  );
}

function DescriptionFieldTemplate({ description, id }: DescriptionFieldProps) {
  if (!description) {
    return null;
  }

  return <Typography id={id}>{description}</Typography>;
}

export type DocumentDownloadFunction = (
  id: string,
) => Promise<{ file: Blob; id: string; title: string }>;

function DocumentField({
  formData,
  formContext,
  registry,
  uiSchema,
  idSchema,
  schema,
}: FieldProps) {
  const value =
    (formData as
      | Partial<DocumentProps & { sharepointFileId: string }>
      | undefined) ?? {};
  const documentId = resolveDocumentId(value);
  const documentName = typeof value.name === 'string' ? value.name : '';
  const { label } = schema as { label?: unknown };
  const documentLabel = typeof label === 'string' ? label : undefined;

  const classes = useStyles();

  const profile = useProfile();

  const options = getUiOptions(uiSchema);

  const Title = getTemplate('TitleFieldTemplate', registry, options);

  const [modal, setModal] = useState(false);
  const [customData, setCustomData] = useState<{
    file: Blob;
    id: string;
    title: string;
  } | null>(null);

  // Check for custom download function in options or formContext
  const customDownload = (options.downloadFunction ||
    (formContext as { downloadFunction?: DocumentDownloadFunction })
      ?.downloadFunction) as DocumentDownloadFunction | undefined;

  // Only use the form context download if no custom download is provided
  const formContextValue = useForm();
  const [defaultDownloadFn, { data: defaultData, loading: defaultLoading }] =
    customDownload
      ? [null, { data: null, loading: false }]
      : formContextValue?.download?.('DOCUMENT') ?? [
          null,
          { data: null, loading: false },
        ];

  const handleClick = async () => {
    if (!documentId) {
      return;
    }

    if (
      !isDocumentViewerEnabled() &&
      typeof value.sharepointFileId === 'string'
    ) {
      const url = await getSharepointDownloadUrl(
        profile?.contact?.ContactID,
        value.sharepointFileId,
      );

      if (url) {
        window.location.href = url;

        return;
      }
    }

    if (customDownload) {
      try {
        const result = await customDownload(documentId);
        setCustomData(result);
      } catch {
        /* empty */
      }
    } else if (defaultDownloadFn) {
      defaultDownloadFn(documentId);
    }
    setModal(true);
  };

  const closeModal = () => {
    setModal(false);
  };

  // Use custom data if custom download is provided, otherwise use default
  const data = customDownload ? customData : defaultData;

  return (
    <div className={classes.documentWrapper}>
      {schema.title && (
        <Title
          id={titleId(idSchema)}
          registry={registry}
          schema={schema}
          title={schema.title}
          uiSchema={uiSchema}
        />
      )}
      <Document
        lowercase
        id={documentId}
        closeModal={closeModal}
        data={data}
        handleClick={handleClick}
        label={documentLabel}
        loading={modal && !customDownload && defaultLoading}
        modal={modal}
        name={documentName}
      />
    </div>
  );
}

function ErrorListTemplate(props: ErrorListProps) {
  const { errors } = props;

  const uniqueMessages = Array.from(
    new Set(errors.map((error) => error.message)),
  );

  return (
    <div data-error-list>
      <InfoBox>
        <ul>
          {uniqueMessages.map((message) => (
            <li key={message} data-testid="form-validation-error-item">
              {message}
            </li>
          ))}
        </ul>
      </InfoBox>
    </div>
  );
}

export function ExpressionOfWishFieldItemTemplate({
  registry,
  formRef,
  ...props
}: ArrayFieldTemplateItemType & { formRef: RefObject<JsonForm> }) {
  const [modalOpen, setModalOpen] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const classes = useStyles();

  const closeModal = () => {
    if (!isEditing) {
      props.onDropIndexClick(props.index)();
    }
    setIsEditing(false);
    setModalOpen(false);
  };

  const openModal = () => {
    setModalOpen(true);
  };

  const ArrayFieldItemTemplate = getTemplate(
    'ArrayFieldItemTemplate',
    registry,
  );

  const { formData, onChange } = props.children
    .props as BaseInputTemplateProps & {
    formData: {
      allocation: number;
      beneficiaryType: 'INDIVIDUAL' | 'TRUST' | 'CHARITY';
      fullName: string;
      trustName: string;
      charityName: string;
    };
  };

  const validation = formRef.current?.validate(formData, props.schema);
  const hasError =
    (validation?.errors && validation.errors?.length > 0) || false;

  const handleSubmitItem = () => {
    formRef.current?.validateForm();
    if (!hasError) {
      setModalOpen(false);
    }
  };

  const getName = () => {
    switch (formData.beneficiaryType) {
      case 'INDIVIDUAL':
        return formData.fullName || `Client name ${props.index}`;
      case 'TRUST':
        return formData.trustName || `Trust name ${props.index}`;
      case 'CHARITY':
        return formData.charityName || `Charity name ${props.index}`;
      default:
        return '';
    }
  };

  return (
    <>
      {formData && (
        <div className={classes.arrayItem}>
          <div>
            <Typography>{getName()}</Typography>
            <div className={classes.errorButton}>
              <Button
                className={classes.editButton}
                variant="text"
                onClick={() => {
                  setIsEditing(true);
                  openModal();
                }}
              >
                Edit
              </Button>
              {hasError && <ErrorIcon color="primary" />}
            </div>
          </div>
          <div className={classes.allocationWrapper}>
            <NumberFormat
              {...props}
              fixedDecimalScale
              id={props.key}
              className={[classes.textbox, 'darkBackground'].join(' ')}
              InputProps={{
                disableUnderline: true,
                endAdornment: <span>%</span>,
              }}
              customInput={TextField}
              decimalScale={2}
              variant="standard"
              defaultValue={0}
              value={formData.allocation}
              onValueChange={({ floatValue }) =>
                onChange({ ...formData, allocation: floatValue })
              }
            />
            <IconButton
              aria-label="delete"
              color="primary"
              onClick={props.onDropIndexClick(props.index)}
            >
              <DeleteOutlinedIcon fontSize="large" />
            </IconButton>
          </div>
        </div>
      )}
      <Modal
        maxWidth="md"
        open={modalOpen}
        title="Add a beneficiary"
        closeHandler={closeModal}
      >
        <ArrayFieldItemTemplate
          {...props}
          registry={registry}
          hasToolbar={false}
        />
        <div className={classes.buttonBar}>
          <Button variant="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button onClick={handleSubmitItem}>Add beneficiary</Button>
        </div>
      </Modal>
    </>
  );
}

export function ExpressionOfWishFieldTemplate({
  items,
  canAdd,
  onAddClick,
  formRef,
}: ArrayFieldTemplateProps & {
  formRef: RefObject<
    JsonForm<{ wishes: { allocation: number }[]; totalAllocation: number }>
  >;
}) {
  const classes = useStyles();
  const [totalAllocation, setTotalAllocation] = useState(0);

  const updateTotalAllocation = useCallback(() => {
    if (formRef.current?.state?.formData) {
      const total = formRef.current.state.formData?.wishes?.reduce(
        (acc: number, wish) => acc + (wish.allocation || 0),
        0,
      );
      setTotalAllocation(total);
      formRef.current.setState({
        formData: {
          ...formRef.current.state.formData,
          totalAllocation: total,
        },
      });
    }
  }, [formRef]);

  useEffect(() => {
    updateTotalAllocation();
  }, [items, updateTotalAllocation]);

  const getAllocationColour = () => {
    if (totalAllocation < 100) return 'warning';
    if (totalAllocation > 100) return 'danger';
    return 'success';
  };

  return (
    <>
      {items?.length > 0 ? (
        <>
          {[
            { name: 'Individuals', type: 'INDIVIDUAL' },
            { name: 'Trusts', type: 'TRUST' },
            { name: 'Charities', type: 'CHARITY' },
          ].map(({ name, type }) => (
            <div className={classes.arrayWrapper} key={type}>
              {items.some(
                (item) =>
                  (
                    item.children.props as {
                      formData?: { beneficiaryType?: string };
                    }
                  ).formData?.beneficiaryType === type,
              ) && (
                <>
                  <Typography className={classes.arrayTitle}>{name}</Typography>
                  {type === 'CHARITY' && (
                    <Typography>
                      I wish to nominate the following charities to receive lump
                      sums if there is no surviving dependant
                    </Typography>
                  )}
                </>
              )}{' '}
              {items
                .filter(
                  (item) =>
                    (
                      item.children.props as {
                        formData?: { beneficiaryType?: string };
                      }
                    ).formData?.beneficiaryType === type,
                )
                .map((itemProps) => (
                  <ExpressionOfWishFieldItemTemplate
                    {...itemProps}
                    formRef={formRef}
                  />
                ))}
            </div>
          ))}
          <div className={classes.totalWrapper}>
            <Typography className={classes.title}>
              Total <span>(must equal 100%)</span>
            </Typography>
            <div className={classes.allocationTotalWrapper}>
              <Typography
                className={[
                  classes.allocationTotal,
                  getAllocationColour(),
                ].join(' ')}
              >
                {totalAllocation.toFixed(2)}%
              </Typography>
              {totalAllocation > 100 && (
                <ErrorIcon color="primary" fontSize="large" />
              )}
              {totalAllocation === 100 && (
                <DoneIcon htmlColor="#5AD2A0" fontSize="large" />
              )}
            </div>
          </div>
        </>
      ) : (
        <Typography className={classes.helpText} align="center">
          No beneficiaries nominated
        </Typography>
      )}

      {canAdd && (
        <Button onClick={onAddClick} variant="secondary" color="primary">
          Add a beneficiary
        </Button>
      )}
    </>
  );
}

function FieldTemplate(props: FieldTemplateProps) {
  const classes = useStyles();

  const {
    children,
    description,
    displayLabel,
    hidden,
    id,
    label,
    registry,
    schema,
    uiSchema,
  } = props;

  if (hidden) {
    return <div className={classes.hidden}>{children}</div>;
  }

  const uiOptions = getUiOptions(uiSchema);

  const Description = getTemplate(
    'DescriptionFieldTemplate',
    registry,
    uiOptions,
  );

  const Wrapper = getTemplate('WrapIfAdditionalTemplate', registry, uiOptions);

  if (schema.type === 'null') {
    return <>{children}</>;
  }

  // "Search" field style
  if (uiOptions.search && schema.type !== 'boolean') {
    return (
      <Grid container spacing={2} alignItems="center">
        <Grid item xs>
          <SearchIcon className={classes.searchIcon} color="inherit" />

          {children}
        </Grid>
      </Grid>
    );
  }

  // "Inline" field style for booleans (checkboxes) - requires explicit opt-in
  // Use 'inlineCheckbox: true' in ui:options to enable this layout
  if (schema.type === 'boolean' && uiOptions.inlineCheckbox) {
    return (
      <div className={classes.row}>
        <Grid container alignItems="flex-start">
          <Grid item xs={12} sm={4} />

          <Grid item xs>
            {children}
          </Grid>
        </Grid>
      </div>
    );
  }

  // "Inline" field style (excludes booleans for backwards compatibility)
  if (uiOptions.inline && schema.type !== 'boolean') {
    const isReadOnly = schema.readOnly === true;

    return (
      <div className={classes.row}>
        <Grid container alignItems="flex-start">
          <Grid
            item
            xs={12}
            sm={4}
            className={
              isReadOnly ? classes.inlineLabelReadOnly : classes.inlineLabel
            }
          >
            {displayLabel && (
              <InputLabel className={classes.label} htmlFor={id}>
                {label}
              </InputLabel>
            )}
          </Grid>

          <Grid item xs>
            {children}
          </Grid>
        </Grid>
      </div>
    );
  }

  // "Signature" field style
  if (uiOptions.widget === 'signature') {
    return (
      <div className={classes.parent}>
        {displayLabel && (
          <>
            <Typography className={classes.title} variant="body1">
              {label}
            </Typography>

            {description && (
              <Description
                id={descriptionId(id)}
                description={description}
                schema={schema}
                uiSchema={uiSchema}
                registry={registry}
              />
            )}
          </>
        )}

        <div>{children}</div>
      </div>
    );
  }

  return (
    <Wrapper {...props}>
      {displayLabel && <InputLabel htmlFor={id}>{label}</InputLabel>}

      {children}
    </Wrapper>
  );
}

interface FormInternalState {
  loading: boolean;
}

const FormInternalContext = createContext<FormInternalState>({
  loading: false,
});

interface ComponentProps {
  children?: ReactNode;
}

const markdownComponents = (classes: ReturnType<typeof useStyles>) => ({
  p: ({ children }: ComponentProps) => <Typography>{children}</Typography>,
  ul: ({ children }: ComponentProps) => (
    <List className={classes.list}>{children}</List>
  ),
  ol: ({ children }: ComponentProps) => (
    <List component="ol" className={classes.orderedList}>
      {children}
    </List>
  ),
  li: ({ children }: ComponentProps) => (
    <ListItem className={classes.listItem}>
      <ListItemText>{children}</ListItemText>
    </ListItem>
  ),
  a: ({ children, ...otherProps }: ComponentProps) => (
    <Link {...otherProps} target="_blank">
      {children}
    </Link>
  ),
});

function MarkdownWidget({ value }: WidgetProps) {
  const classes = useStyles();
  return (
    <ReactMarkdown components={markdownComponents(classes)}>
      {value}
    </ReactMarkdown>
  );
}

function ModalWidget({ value, options }: WidgetProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const classes = useStyles();

  const openModal = () => {
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  return (
    <>
      <Button variant="secondary" size="large" onClick={openModal}>
        {options.modalButton as string}
      </Button>
      <Modal
        maxWidth="xl"
        open={modalOpen}
        title={options.modalTitle as string}
        closeHandler={closeModal}
      >
        <ReactMarkdown components={markdownComponents(classes)}>
          {value}
        </ReactMarkdown>
      </Modal>
    </>
  );
}

function MonetaryValueWidget({
  disabled,
  id,
  placeholder,
  required,
  readonly,
  value,
  onChange,
}: WidgetProps) {
  const classes = useStyles();

  return (
    <NumberFormat
      fixedDecimalScale
      thousandSeparator
      id={id}
      className={[classes.textbox, readonly ? 'read-only' : ''].join(' ')}
      InputProps={{
        disableUnderline: true,
        readOnly: readonly,
        inputProps: {
          'data-testid': id,
        },
      }}
      customInput={TextField}
      decimalScale={2}
      prefix={currencySymbol('GBP')}
      placeholder={placeholder}
      disabled={disabled}
      required={required}
      variant="standard"
      value={value as string | number}
      onValueChange={({ floatValue }) => onChange(floatValue)}
    />
  );
}

function NullField({ onChange }: FieldProps) {
  useEffect(() => {
    onChange(null);
  }, [onChange]);

  return null;
}

function ObjectFieldTemplate({
  description,
  idSchema,
  properties,
  registry,
  schema,
  title,
  uiSchema,
}: ObjectFieldTemplateProps) {
  const classes = useStyles();

  const options = getUiOptions(uiSchema);

  const Title = getTemplate('TitleFieldTemplate', registry, options);

  const Description = getTemplate(
    'DescriptionFieldTemplate',
    registry,
    options,
  );

  if (options.inline) {
    return (
      <>
        {title && (
          <Title
            id={titleId(idSchema)}
            registry={registry}
            schema={schema}
            title={title}
            uiSchema={uiSchema}
          />
        )}

        {description && (
          <Description
            id={descriptionId(idSchema)}
            description={description}
            schema={schema}
            uiSchema={uiSchema}
            registry={registry}
          />
        )}

        {properties.map((prop) => prop.content)}
      </>
    );
  }

  return (
    <div id={idSchema.$id} className={classes.parent}>
      {title && (
        <Title
          id={titleId(idSchema)}
          registry={registry}
          schema={schema}
          title={title}
          uiSchema={uiSchema}
        />
      )}

      {description && (
        <Description
          id={descriptionId(idSchema)}
          description={description}
          schema={schema}
          uiSchema={uiSchema}
          registry={registry}
        />
      )}

      {properties.map((prop) => prop.content)}
    </div>
  );
}

function RadioWidget({
  options,
  value,
  disabled,
  readonly,
  onBlur,
  onFocus,
  onChange,
  id,
  required,
  registry,
  schema,
  uiSchema,
}: WidgetProps) {
  const classes = useStyles();

  const {
    checkbox,
    radioBoxes,
    enumOptions,
    enumDisabled,
    inline,
    emptyValue,
  } = options as {
    checkbox?: boolean;
    radioBoxes?: boolean;
    enumOptions?: Array<{ value: unknown; label: string }>;
    enumDisabled?: unknown[];
    inline?: boolean;
    emptyValue?: unknown;
  };

  const row = options ? inline : false;

  const selectedIndex = enumOptionsIndexForValue(value, enumOptions) ?? null;

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      onBlur(
        id,
        enumOptionsValueForIndex(
          (event.target as HTMLInputElement).value,
          enumOptions,
          emptyValue,
        ),
      );
    },
    [onBlur, id, enumOptions, emptyValue],
  );

  const handleChange = useCallback(
    (_: ChangeEvent<HTMLInputElement>, val: string) => {
      onChange(enumOptionsValueForIndex(val, enumOptions, emptyValue));
    },
    [emptyValue, enumOptions, onChange],
  );

  const handleChangeSingle = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(
        enumOptionsValueForIndex(event.target.value, enumOptions, emptyValue),
      );
    },
    [emptyValue, enumOptions, onChange],
  );

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      onFocus(
        id,
        enumOptionsValueForIndex(
          (event.target as HTMLInputElement).value,
          enumOptions,
          emptyValue,
        ),
      );
    },
    [onFocus, id, enumOptions, emptyValue],
  );

  if (checkbox) {
    const Title = getTemplate('TitleFieldTemplate', registry, options);

    const Description = getTemplate(
      'DescriptionFieldTemplate',
      registry,
      options,
    );

    return (
      <div id={id} className={classes.parent}>
        {schema.title && (
          <Title
            id={titleId(id)}
            registry={registry}
            schema={schema}
            title={schema.title}
            uiSchema={uiSchema}
          />
        )}

        {schema.description && (
          <Description
            id={descriptionId(id)}
            description={schema.description}
            schema={schema}
            uiSchema={uiSchema}
            registry={registry}
          />
        )}

        {Array.isArray(enumOptions) &&
          enumOptions.map((option, index) => {
            const itemDisabled =
              Array.isArray(enumDisabled) &&
              enumDisabled.indexOf(option.value as string) !== -1;

            return (
              <Box className={classes.wrapper} key={index}>
                <div className={classes.checkbox}>
                  <input
                    id={optionId(id, index)}
                    data-testid={optionId(id, index)}
                    className="input"
                    type="radio"
                    name={id}
                    checked={option.value === value}
                    required={required}
                    disabled={disabled || itemDisabled || readonly}
                    value={String(index)}
                    onChange={handleChangeSingle}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    aria-describedby={ariaDescribedByIds(id)}
                  />

                  <label className="label" htmlFor={optionId(id, index)}>
                    {labelValue(<span>{option.label}</span>)}
                  </label>
                </div>
              </Box>
            );
          })}
      </div>
    );
  }

  if (radioBoxes) {
    const Title = getTemplate('TitleFieldTemplate', registry, options);

    const Description = getTemplate(
      'DescriptionFieldTemplate',
      registry,
      options,
    );

    return (
      <div id={id} className={classes.parent}>
        {schema.title && (
          <Title
            id={titleId(id)}
            registry={registry}
            schema={schema}
            title={schema.title}
            uiSchema={uiSchema}
          />
        )}

        {schema.description && (
          <Description
            id={descriptionId(id)}
            description={schema.description}
            schema={schema}
            uiSchema={uiSchema}
            registry={registry}
          />
        )}

        {Array.isArray(enumOptions) &&
          enumOptions.map((option, index) => {
            const itemDisabled =
              Array.isArray(enumDisabled) &&
              enumDisabled.indexOf(option.value as string) !== -1;

            return (
              <Box className={classes.radioBoxWrapper} key={index}>
                <FormControlLabel
                  control={
                    <Radio
                      id={optionId(id, index)}
                      inputProps={{
                        ['data-testid' as string]: optionId(id, index),
                      }}
                      name={id}
                      checked={option.value === value}
                      onChange={handleChangeSingle}
                      onBlur={handleBlur}
                      onFocus={handleFocus}
                      value={String(index)}
                    />
                  }
                  label={option.label}
                  disabled={disabled || itemDisabled || readonly}
                />
              </Box>
            );
          })}
      </div>
    );
  }

  return (
    <RadioGroup
      id={id}
      name={id}
      value={selectedIndex}
      row={row}
      onBlur={handleBlur}
      onChange={handleChange}
      onFocus={handleFocus}
      aria-describedby={ariaDescribedByIds(id)}
    >
      {Array.isArray(enumOptions) &&
        enumOptions.map((option, index) => {
          const itemDisabled =
            Array.isArray(enumDisabled) &&
            enumDisabled.indexOf(option.value as string) !== -1;

          return (
            <FormControlLabel
              control={
                <Radio
                  id={optionId(id, index)}
                  name={id}
                  inputProps={{
                    ['data-testid' as string]: optionId(id, index),
                  }}
                />
              }
              className={classes.radio}
              label={option.label}
              value={String(index)}
              key={index}
              disabled={disabled || itemDisabled || readonly}
            />
          );
        })}
    </RadioGroup>
  );
}

function SignatureWidget({ value, onChange, formContext }: WidgetProps) {
  const profile = useProfile() as {
    setSignature?: (value: string) => void;
    signature?: string;
  } | null;
  const context = formContext as
    | {
        setSignature?: (value: string) => void;
        signature?: string;
      }
    | undefined;
  const setSignature =
    profile?.setSignature ?? context?.setSignature ?? (() => {});
  const signature = profile?.signature ?? context?.signature;

  const onSubmit = () => {
    onChange(true);
  };

  return (
    <Signature
      disableEdit
      displayOnly={!!value}
      initialValue={signature}
      onSign={setSignature}
      onSubmit={onSubmit}
    />
  );
}

function SubmitButton<T>({ uiSchema }: SubmitButtonProps<T>) {
  const classes = useStyles();

  const { loading } = useContext(FormInternalContext);

  const { norender, props, submitText } = getSubmitButtonOptions(uiSchema);

  if (norender) {
    return null;
  }

  if (props?.inline) {
    return (
      <Button
        type="submit"
        variant="primary"
        loading={loading}
        disabled={loading}
      >
        {submitText}
      </Button>
    );
  }

  return (
    <div className={classes.buttonBar}>
      <Button
        type="submit"
        variant="primary"
        loading={loading}
        disabled={loading}
      >
        {submitText}
      </Button>
    </div>
  );
}

function TitleFieldTemplate({ id, title, uiSchema }: TitleFieldProps) {
  const classes = useStyles();

  const readOnly = uiSchema?.['ui:readonly'];

  return (
    <Typography id={id} className={classes.title} variant="body1">
      {title}

      {readOnly && (
        <ReadOnlyTooltip readonly={readOnly}>
          <InfoIcon className={classes.infoIcon} />
        </ReadOnlyTooltip>
      )}
    </Typography>
  );
}

function WrapIfAdditionalTemplate<T>({
  children,
  classNames,
  schema,
  uiSchema,
}: WrapIfAdditionalTemplateProps<T>) {
  const classes = useStyles();

  const additional = ADDITIONAL_PROPERTY_FLAG in schema;

  if (additional) {
    // TODO: Populate template if this is ever needed
    return null;
  }

  const noBackground =
    classNames?.includes('field-object') ||
    (uiSchema && uiSchema['ui:options']?.checkbox) ||
    (uiSchema && uiSchema['ui:options']?.radioBoxes) ||
    (uiSchema && uiSchema['ui:options']?.noBackground) ||
    schema.type === 'array' ||
    (uiSchema &&
      uiSchema['ui:widget'] === 'radio' &&
      uiSchema?.['ui:options']?.checkbox);

  if (noBackground) {
    return <div className={classNames}>{children}</div>;
  }

  return <Box className={classes.wrapper}>{children}</Box>;
}

const validator = customizeValidator();

ajvErrors(validator.ajv);

export type FormData<T = GenericObjectType> = IChangeEvent<T, RJSFSchema>;

type OnSubmit<T> = (data: FormData<T>) => void | Promise<void>;

interface FormProps<T = GenericObjectType> {
  children?: ReactNode;
  customValidate?: CustomValidator<T, RJSFSchema>;
  formContext?: GenericObjectType;
  formData?: T;
  loading?: boolean;
  schema: RJSFSchema;
  ui?: UiSchema<T>;
  onChange?: (data: FormData<T>) => void;
  onSubmit: OnSubmit<T>;
}

function Form<T>({
  children,
  customValidate,
  formContext,
  formData,
  loading = false,
  schema,
  ui,
  onChange,
  onSubmit,
}: FormProps<T>) {
  const classes = useStyles();

  const { templates: defaultTemplates } = getDefaultRegistry<T, RJSFSchema>();

  const ref = createRef<JsonForm>();

  const fields = {
    document: DocumentField,
    NullField,
  };

  const ArrayFieldTemplateWrapper = useCallback(
    (props: ArrayFieldTemplateProps) => (
      <ArrayFieldTemplate {...props} formRef={ref} />
    ),
    [ref],
  );

  const templates = {
    ...defaultTemplates,
    ArrayFieldTemplate: ArrayFieldTemplateWrapper,
    BaseInputTemplate,
    ButtonTemplates: {
      ...defaultTemplates.ButtonTemplates,
      SubmitButton,
    },
    DescriptionFieldTemplate,
    ErrorListTemplate,
    FieldTemplate,
    ObjectFieldTemplate,
    TitleFieldTemplate,
    WrapIfAdditionalTemplate,
  };

  const widgets = {
    CheckboxWidget,
    DateWidget,
    MarkdownWidget,
    ModalWidget,
    monetaryValue: MonetaryValueWidget,
    password: PasswordWidget,
    RadioWidget,
    signature: SignatureWidget,
  };

  const inline = ui?.['ui:options']?.inline || ui?.['ui:options']?.search;

  const state = useMemo(
    () => ({
      loading,
    }),
    [loading],
  );

  const [currentFormData, setCurrentFormData] = useState(() => formData);

  // Sync external formData prop changes to internal state
  useEffect(() => {
    setCurrentFormData(formData);
  }, [formData]);

  const enhancedFormContext = useMemo(
    () => ({
      ...formContext,
      formData: currentFormData,
    }),
    [formContext, currentFormData],
  );

  const handleFormChange = useCallback(
    (data: IChangeEvent<T, RJSFSchema>) => {
      setCurrentFormData(data.formData);
      onChange?.(data);
    },
    [onChange],
  );

  const scrollToErrors = () => {
    const form = ref.current?.formElement.current as HTMLFormElement | null;

    form
      ?.querySelector('[data-error-list]')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <FormInternalContext.Provider value={state}>
      <JsonForm
        noHtml5Validate
        experimental_customMergeAllOf={
          merge as Experimental_CustomMergeAllOf<RJSFSchema>
        }
        showErrorList="top"
        liveValidate={false}
        className={[classes.form, inline ? 'inline' : ''].join(' ')}
        omitExtraData
        schema={schema}
        fields={fields}
        uiSchema={ui}
        formData={currentFormData}
        formContext={enhancedFormContext}
        validator={validator as AJV8Validator<T, RJSFSchema>}
        customValidate={customValidate}
        templates={templates}
        widgets={widgets}
        onSubmit={onSubmit}
        onChange={handleFormChange}
        onError={scrollToErrors}
        ref={ref}
      >
        {children}
      </JsonForm>
    </FormInternalContext.Provider>
  );
}

export default Form;
